import React, { useEffect, useState } from 'react';
import { CreateFormFeatureDialog } from 'components';
import {
    USER_GROUPS,
    walletsAndGroupsCommonStyles,
    walletsAndGroupsConfig,
} from 'pages/wallets-and-groups/wallets-and-groups-helper';
import { useNavigate } from 'react-router-dom';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { Controller, useForm } from 'react-hook-form';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import {
    combinedSystemAndUserWalletsData,
    createAndUpdateUserGroupDefaults,
    CreateAndUpdateUserGroupFormData,
    CreateAndUpdateUserGroupSchema,
    GroupMembersData,
} from './create-or-edit-user-group-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUserGroup, useWalletList } from '../../../../hooks';
import { useAuth, usePermission } from '../../../../core';
import { Grid } from '@mui/material';
import { TextField } from '@biq/apex-ui';
import { AutoCompleteGroupMembersField } from './AutoCompleteGroupMembersField';
import { DescriptionTextField } from '../description-field/DescriptionTextField';

export const CreateUserGroup = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [groupMembersData, setGroupMembersData] = useState<
        GroupMembersData[]
    >([]);
    const { mutate: uploadUserGroup } = useUserGroup();
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();
    const navigate = useNavigate();
    const { hasCurrentPermissionAndRole } = usePermission();
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm<CreateAndUpdateUserGroupFormData>({
        mode: 'all',
        resolver: yupResolver(CreateAndUpdateUserGroupSchema),
        defaultValues: createAndUpdateUserGroupDefaults,
    });

    useEffect(() => {
        if (!walletsLoading && walletsData) {
            const combinedGroupMembers =
                combinedSystemAndUserWalletsData(walletsData);
            setGroupMembersData(combinedGroupMembers);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walletsLoading]);

    const createUserGroup = (data: CreateAndUpdateUserGroupFormData): void => {
        setIsLoading(true);
        uploadUserGroup(
            {
                token: getToken(),
                name: data.name.trim(),
                description: data.description.trim(),
                group: data.group.map((item: any) => item.id),
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    setTimeout(() => {
                        navigate(
                            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=${USER_GROUPS}`
                        );
                    }, 500);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onSubmit = (): void => {
        const permission = hasCurrentPermissionAndRole(
            'createMetadata',
            'sdzGovernance'
        );
        if (permission) {
            handleSubmit(createUserGroup)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCloseButton = (): void => {
        navigate(
            `${WALLETS_AND_GROUPS_PATHS.WALLETS_AND_GROUPS_PATH}?option=${USER_GROUPS}`
        );
    };

    const handleBackButton = (): void => {
        navigate(
            `${WALLETS_AND_GROUPS_PATHS.CREATE_NEW_WALLETS_AND_GROUPS_PATH}?option=${USER_GROUPS}`
        );
    };

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={onSubmit}
            isLoading={isLoading}
            handleCloseButton={handleCloseButton}
            handleBackButton={handleBackButton}
            listConfig={walletsAndGroupsConfig}
        >
            <Grid sx={walletsAndGroupsCommonStyles.mainContainer}>
                <Grid sx={walletsAndGroupsCommonStyles.textFieldContainer}>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Group Name'
                                required
                                onBlur={(): void => {
                                    setValue('name', field.value.trimStart());
                                }}
                                sx={
                                    walletsAndGroupsCommonStyles.createTextFieldStyles
                                }
                                error={!!errors.name}
                                errorText={
                                    errors.name ? errors.name.message : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <DescriptionTextField
                    control={control}
                    rows={2}
                    error={errors.description}
                    required={false}
                />
                <Grid sx={walletsAndGroupsCommonStyles.containerWidth}>
                    <AutoCompleteGroupMembersField
                        name={'group'}
                        label={'Group Members'}
                        control={control}
                        options={groupMembersData}
                        fieldValue={watch('group')}
                        error={errors.group}
                    />
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
